import { useEffect, useImperativeHandle, useState } from 'react';
import { isMobile } from '/@/utils';

type UseMapProps = {
    ref: React.Ref<google.maps.Map | null>;
    mapRef: React.RefObject<HTMLDivElement>;
    zoomControls: boolean;
    zoomPosition: 'TOP_RIGHT' | 'BOTTOM_RIGHT';
};

/**
 * @returns {google.maps.Map}
 */
export const useMap = ({
    ref,
    mapRef,
    zoomControls = true,
    zoomPosition = 'TOP_RIGHT',
}: UseMapProps): google.maps.Map | null => {
    const [map, setMap] = useState<google.maps.Map | null>(null);

    // Expose the map instance directly
    useImperativeHandle(ref, () => map, [map]);

    useEffect(() => {
        if (typeof ref === 'function') ref(map);
        else if (ref?.current) (ref as React.MutableRefObject<google.maps.Map | null>).current = map;
    }, [map, ref]);

    useEffect(() => {
        // Check if Google Maps is loaded before creating the map
        if (typeof google === 'undefined' || typeof google.maps === 'undefined' || !mapRef.current) {
            return;
        }

        const options = {
            mapId: 'DEMO_MAP_ID',
            mapTypeId: 'hybrid',
            disableDefaultUI: true,
            zoomControl: !isMobile() && zoomControls,
            zoomControlOptions: {
                position: google.maps.ControlPosition[zoomPosition as keyof typeof google.maps.ControlPosition],
            },
        };

        const newMap = new google.maps.Map(mapRef.current, options);

        // Set the map immediately so components can use it
        if (typeof ref === 'function') ref(newMap);
        else if (ref?.current) (ref as React.MutableRefObject<google.maps.Map | null>).current = newMap;
        setMap(newMap);
    }, [zoomControls, zoomPosition, mapRef, ref]);

    return map;
};
